import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Faq = () => {
  // FAQ data with questions and answers
  const faqs = [
    {
      id: 1,
      question: "What types of websites do you build?",
      answer: "I specialize in creating custom, responsive websites for various business needs including landing pages, business websites, e-commerce stores, and portfolio sites. Every project is custom-designed to meet your specific business goals and target audience preferences."
    },
    {
      id: 2,
      question: "How long does it take to complete a website?",
      answer: "The timeline varies depending on the project scope and complexity. A typical landing page can be completed in 2-3 weeks, while a more complex website might take 4-8 weeks. During our initial consultation, I'll provide you with a more accurate timeline based on your specific requirements."
    },
    {
      id: 3,
      question: "Do you handle website maintenance after launch?",
      answer: "Yes, I offer ongoing website maintenance services to ensure your site remains secure, up-to-date, and performing optimally. Maintenance plans include regular updates, security monitoring, performance optimization, and technical support."
    },
    {
      id: 4,
      question: "How do you approach SEO in your web development?",
      answer: "SEO is integrated throughout the development process. I implement technical SEO best practices, optimize site structure and loading speeds, and ensure your website is built with clean, semantic code. Additional services include keyword research, on-page SEO implementation, and performance tracking."
    },
    {
      id: 5,
      question: "What is your payment structure?",
      answer: "I typically require a 50% deposit to begin work, with the remaining balance due upon project completion. For larger projects, we can establish a milestone-based payment schedule. All payment terms are clearly outlined in our project agreement."
    },
    {
      id: 6,
      question: "Do you work with clients remotely?",
      answer: "Yes, I work with clients globally. Through effective communication tools, regular updates, and collaborative platforms, I ensure a smooth workflow regardless of your location. This approach has allowed me to successfully complete projects for clients across different countries and time zones."
    },
    {
      id: 7,
      question: "Will my website be mobile-friendly?",
      answer: "Absolutely! All websites I build are fully responsive and optimized for mobile devices. With mobile traffic accounting for more than half of all web traffic, I ensure your site looks and functions flawlessly across all screen sizes and devices."
    },
    {
      id: 8,
      question: "Can you help with website content?",
      answer: "While I don't provide content creation services directly, I can guide you on content strategy and structure to maximize impact. If needed, I can recommend professional copywriters and content creators from my network who specialize in creating engaging, SEO-friendly content for websites."
    }
  ];

  // State to track which FAQ item is currently open
  const [activeIndex, setActiveIndex] = useState(null);
  // Track if we're on mobile for optimized animations
  const [isMobile, setIsMobile] = useState(false);

  // Set mobile state based on window width
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Toggle function to expand/collapse FAQ items
  const toggleFaq = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="container mx-auto px-4 py-20">
      <div className="relative mb-16 text-center">
        <div className="absolute left-1/2 top-1/2 -z-10 h-36 w-36 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/0 blur-2xl" />
        <h2 className="relative text-4xl font-bold text-white md:text-5xl lg:text-6xl">
          Frequently <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">Asked Questions</span>
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-400">
          Find answers to common questions about my web development services and process.
        </p>
      </div>

      <div className="mx-auto max-w-3xl">
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={faq.id}
              className="group relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900/50 transition-all duration-200 hover:border-cyan-500/50"
            >
              <button
                onClick={() => toggleFaq(index)}
                className="flex w-full items-center justify-between p-6 text-left text-white"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${faq.id}`}
              >
                <span className="text-lg font-medium">{faq.question}</span>
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className={`h-4 w-4 text-cyan-500 transform will-change-transform transition-transform duration-10000 ${activeIndex === index ? 'rotate-180' : ''}`} 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path 
                      fillRule="evenodd" 
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" 
                      clipRule="evenodd" 
                    />
                  </svg>
                </span>
              </button>
              <div 
                id={`faq-answer-${faq.id}`}
                className={`${isMobile ? 'transition-[opacity,visibility] duration-200' : 'transition-all duration-300'} will-change-[opacity,height] ${
                  activeIndex === index ? 'opacity-100 visible' : 'opacity-0 invisible h-0'
                }`}
                style={{
                  height: activeIndex === index ? 'auto' : 0,
                }}
              >
                <div className="border-t border-slate-800 p-6 text-slate-400">
                  {faq.answer}
                </div>
              </div>
              <div className={`h-1 bg-gradient-to-r from-cyan-500 to-blue-500 transform will-change-transform transition-transform duration-[1000ms] ${
                activeIndex === index ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`} style={{ transformOrigin: 'left' }}></div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-16 text-center">
        <p className="mb-6 text-lg text-slate-400">
          Still have questions? Feel free to reach out directly.
        </p>
        <Link 
          to="/contact" 
          className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 px-8 py-3 font-semibold text-white transition-all duration-300 hover:brightness-110 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-cyan-500/25"
        >
          Contact Me
          <svg 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            fill="none" 
            className="h-5 w-5 ml-2 transition-transform duration-300 group-hover:translate-x-1"
          >
            <path d="M17 8l4 4m0 0l-4 4m4-4H3" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          </svg>
        </Link>
      </div>
    </div>
  );
};

export default Faq;
